import { useState, useEffect, useRef } from "react";
import {
  MapPin, Globe2, Building2,
  ShoppingBag, AppWindow, Code2, Briefcase, GraduationCap, Home,
  Stethoscope, Sofa, Camera, Syringe, Package, UtensilsCrossed,
  Scissors, BookOpen, Shirt, Store, Shapes, Ticket,
} from "lucide-react";
import { SUPPORTED_PLATFORMS } from "@/components/landing/SocialLogos";
import { pick } from "@/lib/wsCopy";
import { INTERVIEW_COPY, pickSourceAware, isDumpJob } from "@/lib/interviewCopy";

/* Relevant icon per business category (keyed by the backend category id) */
export const CATEGORY_ICONS = {
  d2c_ecommerce: ShoppingBag,
  saas: AppWindow,
  web_agency: Code2,
  professional_services: Briefcase,
  education_big_niche: GraduationCap,
  real_estate_metro: Home,
  dentist_metro: Stethoscope,
  interior_designer_metro: Sofa,
  wedding_photographer_metro: Camera,
  dermatologist_metro: Syringe,
  fmcg_wholesale_distributors: Package,
  restaurant_cafe: UtensilsCrossed,
  salon_barbershop: Scissors,
  tuition_centre: BookOpen,
  garment_wholesale: Shirt,
  local_retail_walkin: Store,
  location_entertainment: Ticket,
  non_metro_local: MapPin,
  other: Shapes,
};

/* Buyer-keyword intent stages → human headers (no jargon past the API boundary).
   Legacy value "pain" folds into "pain_aware". Anything unrecognised falls to "other". */
export const KW_STAGE_GROUPS = [
  { key: "ready", label: "Ready to buy", stages: ["ready"] },
  { key: "researching", label: "Researching / comparing", stages: ["researching"] },
  { key: "pain_aware", label: "Pain / problem", stages: ["pain_aware", "pain"] },
  { key: "other", label: "Other", stages: [] },
];

export const PLATFORM_NAMES = Object.fromEntries(SUPPORTED_PLATFORMS.map((p) => [p.id, p.name]));
export const MAX_SCAN_PLATFORMS = 5;
export const MAX_SCAN_KEYWORDS = 15;

/* ==================================================================== */
/* Brand-generation wait — honest-progress engine + copy (Ravi + Ori)    */
/* ==================================================================== */

// Real milestone ceilings the backend emits, in order. The bar may DRIFT
// toward the next ceiling between polls but stops 2% short and never crosses a
// band before that band's event arrives. 100 only when status==="done".
export const GEN_BANDS = [5, 20, 40, 58, 85, 92, 100];

// The in-flight-job and unconfirmed-draft sessionStorage keys moved to
// lib/brandDraft.js because they are now NAMESPACED PER WORKSPACE — they are
// functions of workspace_id, not constants. See the header there for why.

// Friendly "try again" info from the brand-analysis cooldown unblock time.
export function cooldownRetry(unblocksAt) {
  if (!unblocksAt) return null;
  const t = new Date(unblocksAt).getTime();
  if (!t || t <= Date.now()) return null;
  const mins = Math.max(1, Math.ceil((t - Date.now()) / 60000));
  const at = new Date(t).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  return { mins, at };
}

// Ori's 7 stage lines, keyed to the segment we're CURRENTLY in (chosen by the
// last event type the backend reported). Order 1..7 as events progress.
export const GEN_STAGE_LINES = {
  start:         "Opening up your website…",             // → crawl_done
  crawl_done:    "Seeing exactly what you sell…",         // building the profile
  profile_ready: "Getting to know your ideal customers…", // building stage 1 (ICPs)
  stage1_done:   "Learning what they say right before they buy…",
  stage2_done:   "Writing the exact words they type…",    // stage 3 (keywords)
  stage3_progress: "Writing the exact words they type…",
  stage4_done:   "Keeping only the ones real people actually say…",
  complete:      "Putting it all together for you…",
};

export function genStageKey(stage, percent) {
  if (!stage) return "start";
  if (GEN_STAGE_LINES[stage]) return stage;
  // Unknown/interim event → fall back by percent band.
  if (percent >= 92) return "stage4_done";
  if (percent >= 58) return "stage2_done";
  if (percent >= 40) return "stage1_done";
  if (percent >= 20) return "profile_ready";
  if (percent >= 5) return "crawl_done";
  return "start";
}

export function genStageLine(gen, mobile) {
  // Waiting on the owner is not a stage of work, it is the ABSENCE of work. The
  // stage line must never describe activity here: `stage` is "awaiting_interview"
  // and `percent` is frozen at 20, so the percent-band fallback in genStageKey
  // would otherwise land on "Getting to know your ideal customers…" — a sentence
  // about a stage that has not started and cannot start until the owner answers.
  if (gen?.waiting_on_owner) {
    return pickSourceAware(pick, gen, INTERVIEW_COPY.progressHeld, INTERVIEW_COPY.progressHeldDump, mobile);
  }
  const key = genStageKey(gen?.stage, gen?.percent || 0);
  if (key === "stage3_progress" || (key === "stage2_done" && gen?.stage3)) {
    const s3 = gen?.stage3;
    if (s3 && typeof s3.n === "number" && typeof s3.total === "number") {
      return mobile
        ? `Exact words people type — ${s3.n} of ${s3.total}.`
        : `Writing the exact words people type — ${s3.n} of ${s3.total} sets done.`;
    }
  }
  // "Opening up your website…" is false on the pasted-content path — the owner
  // has just told us they have no site. Pre-existing bug, fixed here rather than
  // inherited: the interview surfaces the same lie one screen later, and leaving
  // this one would mean the two screens disagree about what is being read.
  if (key === "start" && isDumpJob(gen)) return "Reading what you sent me…";
  return GEN_STAGE_LINES[key] || GEN_STAGE_LINES.start;
}

// Overrun reassurance — shown ONLY when the bar has held with no new milestone
// for a while. Rotate A→B; C only near the end. Never fake-advances the bar.
export const GEN_REASSURE = [
  "Still working — your brand's got a lot going on, and I want this right.",
  "Hang tight — the good stuff takes a few extra seconds.",
  "Almost through this part — worth the extra beat, I promise.",
];
export const GEN_REASSURE_MOBILE = "Still working — worth the extra beat.";
export const GEN_OVERRUN_MS = 12000; // stage held this long with no new event → reassure

export function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(
    () => typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!mq) return;
    const on = () => setReduced(mq.matches);
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return reduced;
}

export function useIsMobile() {
  const [mobile, setMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia?.("(max-width: 639px)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia?.("(max-width: 639px)");
    if (!mq) return;
    const on = () => setMobile(mq.matches);
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return mobile;
}

/**
 * Honest, hybrid progress. `realPercent` is the backend's monotonic truth.
 * Returns the width % to render: it eases up toward (next band ceiling − 2) on
 * a decelerating curve, never rewinds, never crosses a band before its event,
 * and only reaches 100 when `done`. Under reduced motion it steps instantly to
 * the real percent (no drift). aria-valuenow should use `realPercent`, not this.
 */
export function useHonestProgress(realPercent, done, reduced) {
  const [shown, setShown] = useState(0);
  const shownRef = useRef(0);

  useEffect(() => {
    const rp = Math.max(0, Math.min(100, realPercent || 0));
    if (done) {
      shownRef.current = 100;
      setShown(100);
      return;
    }
    // Never render below the real milestone.
    if (shownRef.current < rp) {
      shownRef.current = rp;
      setShown(rp);
    }
    if (reduced) {
      // Instant width steps on each real percent — no drift.
      shownRef.current = Math.max(shownRef.current, rp);
      setShown(shownRef.current);
      return;
    }
    const nextCeil = GEN_BANDS.find((b) => b > rp);
    const target = nextCeil == null ? rp : Math.max(rp, nextCeil - 2);
    const id = setInterval(() => {
      const cur = shownRef.current;
      if (cur >= target - 0.05) return;
      const next = cur + (target - cur) * 0.06; // decelerating approach
      shownRef.current = Math.min(next, target);
      setShown(Math.round(shownRef.current * 10) / 10);
    }, 120);
    return () => clearInterval(id);
  }, [realPercent, done, reduced]);

  return shown;
}

/**
 * Track how long the current stage has HELD with no new milestone. Returns the
 * overrun reassurance string (or null). Rotates A→B every ~6s; uses C only near
 * the end. Never advances the bar — this is copy-only.
 */
export function useOverrunCopy(gen, mobile) {
  const [msg, setMsg] = useState(null);
  const markerRef = useRef({ key: "", at: Date.now() });
  const marker = `${gen?.stage || ""}:${gen?.percent ?? 0}:${gen?.stage3?.n ?? ""}`;
  useEffect(() => {
    // Any real change (event type, percent, or stage-3 sub-count) resets dwell.
    markerRef.current = { key: marker, at: Date.now() };
    setMsg(null);
  }, [marker]);
  useEffect(() => {
    if (!gen || gen.status === "done" || gen.status === "error") { setMsg(null); return; }
    // While the job waits on the owner, `stage` and `percent` are frozen BY
    // DESIGN, so the dwell timer below would fire every single time (12s of not
    // moving is guaranteed) and start reassuring "Still working — the good stuff
    // takes a few extra seconds" at a server that is doing nothing at all. The
    // overrun copy exists to explain a SLOW stage; there is no stage here.
    if (gen.waiting_on_owner) { setMsg(null); return; }
    const id = setInterval(() => {
      const held = Date.now() - markerRef.current.at;
      if (held < GEN_OVERRUN_MS) { setMsg(null); return; }
      if (mobile) { setMsg(GEN_REASSURE_MOBILE); return; }
      const nearEnd = (gen.percent || 0) >= 85;
      const step = Math.floor((held - GEN_OVERRUN_MS) / 6000); // rotate every 6s
      const idx = nearEnd ? 2 : step % 2; // A↔B, or C near the end
      setMsg(GEN_REASSURE[idx]);
    }, 1000);
    return () => clearInterval(id);
  }, [gen, mobile]);
  return msg;
}

export const SCOPE_ICONS = { local_city: MapPin, india: Building2, us: Building2, au: Building2, uk: Building2, global: Globe2 };
export const SCOPE_HELPERS = {
  india: "Customers anywhere in India — we focus on Indian conversations.",
  us: "Customers anywhere in the US — we focus on US conversations.",
  au: "Customers anywhere in Australia — we focus on Australian conversations.",
  uk: "Customers anywhere in the UK — we focus on UK conversations.",
  global: "Customers anywhere — no location filter.",
  local_city: "Customers in your city — we focus on your city's local conversations.",
};

export function formatScope(scope) {
  switch (scope) {
    case "local_city": return "My city";
    case "india": return "India";
    case "us": return "United States";
    case "global": return "Global";
    default: return "My market";
  }
}
